'use client';

import { useTranslations } from 'next-intl';
import type { PriceTier } from '@cobrastyle/shared-types';
import { cn } from '@cobrastyle/ui';
import { formatPrice } from '@/lib/format-price';

interface VolumePricingTableProps {
  tiers: PriceTier[];
  /** Current selected quantity, used to highlight the active tier. */
  quantity: number;
}

/**
 * The "Mängdrabatt" panel in the buy box: what the unit price drops to at each
 * quantity threshold, with the tier the current selection reaches picked out.
 *
 * Drawn to the same rules as the panels around it — 4px radius, the recessed
 * ground of the spec grid, and the uppercase micro-label every other panel
 * heading on this page uses. Prices go through the page's shared formatter
 * rather than the design system's, which prints öre the catalogue never quotes:
 * sitting between the spec grid and the gift panel, "3 000,00 kr" was the only
 * price on the page carrying decimals.
 */
export function VolumePricingTable({ tiers, quantity }: VolumePricingTableProps) {
  const t = useTranslations('product');
  if (tiers.length === 0) return null;

  // Active tier = the highest quantity threshold the current selection reaches.
  const activeIndex = tiers.reduce((acc, tier, i) => (quantity >= tier.quantity ? i : acc), -1);

  return (
    <div className="rounded border border-border bg-surface-subtle px-4 py-3.5">
      <p className="mb-1 text-[11px] uppercase tracking-[0.08em] text-muted">
        {t('volumePricing')}
      </p>

      <ul className="divide-y divide-border">
        {tiers.map((tier, i) => (
          <li
            key={tier.quantity}
            className={cn(
              'flex items-center justify-between gap-3 py-2 text-sm',
              i === activeIndex ? 'font-semibold text-accent-700' : 'text-muted',
            )}
          >
            <span>{t('buyQtyPlus', { quantity: tier.quantity })}</span>

            <span className="flex items-baseline gap-2">
              <span className={i === activeIndex ? undefined : 'text-foreground'}>
                {formatPrice(tier.final, tier.currency)}
              </span>
              <span className="text-xs text-muted">{t('perUnitEach')}</span>
              {tier.discount && tier.discount.percent > 0 && (
                <span className="text-xs font-medium text-success-600">
                  {/* Rounded, like the discount badge beside the price. Magento
                      returns a raw float, which rendered as "Spara 21.05%" — a
                      period decimal in a Swedish store, at false precision. */}
                  {t('save', { percent: Math.round(tier.discount.percent) })}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
